import { useEffect, useState } from 'react';

// Backend URL comes from env at build time. If Render (or any static host)
// forgets to set REACT_APP_BACKEND_URL, fall back to the always-on preview
// backend so the live site keeps working. Belt and braces.
const API =
  process.env.REACT_APP_BACKEND_URL ||
  'https://videra-platform.preview.emergentagent.com';

// Static JSON snapshot baked into the build at prebuild time. Used as a
// final fallback so /resources is never empty even if the backend is down.
const STATIC_FALLBACK_INDEX = '/data/articles.json';
const staticArticleUrl = (slug) => `/data/articles/${encodeURIComponent(slug)}.json`;
// One initial attempt + up to N retries. Three retries = four total requests
// with delays [800, 1600, 3200] before attempts 2, 3, 4.
const MAX_RETRIES = 3;
const RETRY_DELAYS_MS = [800, 1600, 3200];

/**
 * Public article API client. Used by /resources index, /resources/:slug and
 * /resources/topic/:cluster pages to fetch articles from the backend.
 *
 * Backend documents use snake_case; the site components use camelCase (e.g.
 * readMinutes). This helper normalises the shape once so downstream views
 * don't need to know about the storage format.
 *
 * Transient 502/503 from the CDN edge during backend restarts is common in
 * preview, so every public fetch retries up to 3 times with backoff.
 */

export function normaliseArticle(doc) {
  if (!doc) return doc;
  return {
    ...doc,
    readMinutes: doc.read_minutes ?? doc.readMinutes ?? 6,
  };
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function fetchWithRetry(path, { allow404 = false } = {}) {
  let lastErr;
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    let retriable = false;
    try {
      const res = await fetch(`${API}${path}`, { headers: { Accept: 'application/json' } });
      if (res.status === 404 && allow404) return null;
      if (res.status >= 500 && res.status < 600) {
        // Retriable upstream error — record and continue the loop.
        lastErr = new Error(`Upstream ${res.status}`);
        retriable = true;
      } else if (!res.ok) {
        // Terminal 4xx (or 3xx unhandled) — never retry.
        throw new Error(`Failed (${res.status})`);
      } else {
        return res.json();
      }
    } catch (err) {
      // Network errors (TypeError from fetch) are retriable. Terminal HTTP
      // errors we've already thrown above are re-raised without retrying.
      if (err instanceof TypeError) {
        lastErr = err;
        retriable = true;
      } else {
        throw err;
      }
    }
    if (!retriable) break;
    if (attempt < MAX_RETRIES) await sleep(RETRY_DELAYS_MS[attempt]);
  }
  throw lastErr || new Error('Failed to load');
}

export async function fetchArticles() {
  try {
    const list = await fetchWithRetry('/api/articles');
    return list.map(normaliseArticle);
  } catch (err) {
    // Backend unreachable — try the static JSON snapshot baked in at build time.
    try {
      const res = await fetch(STATIC_FALLBACK_INDEX, { headers: { Accept: 'application/json' } });
      if (res.ok) {
        const list = await res.json();
        if (Array.isArray(list) && list.length) {
          return list.map(normaliseArticle);
        }
      }
    } catch (_) { /* fall through to original error */ }
    throw err;
  }
}

export async function fetchArticle(slug) {
  try {
    const doc = await fetchWithRetry(`/api/articles/${encodeURIComponent(slug)}`, { allow404: true });
    if (doc) return normaliseArticle(doc);
  } catch (err) {
    // Backend unreachable — try the static per-slug snapshot.
    try {
      const res = await fetch(staticArticleUrl(slug), { headers: { Accept: 'application/json' } });
      if (res.ok) return normaliseArticle(await res.json());
    } catch (_) { /* fall through */ }
    throw err;
  }
  // Backend explicitly returned 404 — check static snapshot too, in case a new
  // article was baked in at build time but hasn't been redeployed to the backend.
  try {
    const res = await fetch(staticArticleUrl(slug), { headers: { Accept: 'application/json' } });
    if (res.ok) return normaliseArticle(await res.json());
  } catch (_) { /* ignore */ }
  return null;
}

export function useArticles() {
  const [state, setState] = useState({ articles: [], loading: true, error: '' });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const articles = await fetchArticles();
        if (!cancelled) setState({ articles, loading: false, error: '' });
      } catch (err) {
        if (!cancelled) setState({ articles: [], loading: false, error: err.message });
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return state;
}

export function useArticle(slug) {
  const [state, setState] = useState({ article: null, loading: true, error: '' });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const article = await fetchArticle(slug);
        if (!cancelled) setState({ article, loading: false, error: '' });
      } catch (err) {
        if (!cancelled) setState({ article: null, loading: false, error: err.message });
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);
  return state;
}

/**
 * Fire-and-forget "I read this article" ping. Never blocks the UI and never
 * throws. Backend increments a counter on the article doc.
 */
export function logArticleView(slug) {
  if (!slug) return;
  try {
    fetch(`${API}/api/articles/${encodeURIComponent(slug)}/view`, {
      method: 'POST',
      keepalive: true,
    }).catch(() => { /* ignore */ });
  } catch (_) { /* ignore */ }
}

/**
 * POST a newsletter signup. Returns { ok: true } on success, throws on
 * validation error so the UI can show a friendly message.
 */
export async function subscribeNewsletter(email, source) {
  const res = await fetch(`${API}/api/newsletter`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, source: source || '' }),
  });
  if (res.status === 422 || res.status === 400) {
    throw new Error('Please enter a valid email address.');
  }
  if (!res.ok) {
    throw new Error('Signup failed. Please try again in a moment.');
  }
  return res.json();
}
